import { DatePipe } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    OnInit,
    QueryList,
    ViewChildren,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { BusyService } from "@components/busy/busy.service";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ProduktionsmallAccordionBodyComponent } from "./accordion/produktionsmall-accordion-body/produktionsmall-accordion-body.component";
import { ProduktionsmallAccordionComponent } from "./accordion/produktionsmall-accordion/produktionsmall-accordion.component";
import { IProduktionsmall, IProduktionsmallRefreshItem, ProduktionsmallRefreshMode } from "./produktionsmall-produktval.models";
import { ProduktionsmallarTexts } from "./produktionsmallar.texts";
import { ProduktionsmallarService } from "./services/produktionsmallar.service";

export type ProduktionsmallarRouteData = {
    isCKMall: boolean;
};

@Component({
    selector: "rask-produktionsmallar",
    styleUrls: ["./produktionsmallar.component.scss"],
    templateUrl: "./produktionsmallar.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        ActionButtonComponent,
        ProduktionsmallAccordionComponent,
        ProduktionsmallAccordionBodyComponent,
        DatePipe
    ]
})
export class ProduktionsmallarComponent implements OnInit {
    activeModal = inject(NgbActiveModal);
    isCKMall = false;
    isModalActive = false;
    mallar: IProduktionsmall[] = [];
    @ViewChildren("mallbodys") mallbodys: QueryList<ProduktionsmallAccordionBodyComponent>;
    texts = ProduktionsmallarTexts.texts;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly changeDetector = inject(ChangeDetectorRef);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly produktionsmallarService = inject(ProduktionsmallarService);
    private readonly route = inject(ActivatedRoute);

    private lastUpdatedMallGuid: string;

    get title() {
        return this.isCKMall ? "Produktionsmallar" : "Mina produktionsmallar";
    }

    addMall(): void {
        if (this.isAllowedToAddRows()) {
            const mall = {
                guid: GuidUtils.newGuid(),
                isNew: true,
                benamning: this.texts.standardNamnPaMall,
                ckLeverantorId: undefined,
                handelssortiment: []
            } as IProduktionsmall;
            this.mallar.unshift(mall);
        }
    }

    canEdit = () => this.authenticationService.canEdit();

    canMallarBeSaved(): boolean {
        const result = this.mallbodys.some(x => x.canBeSaved);
        this.changeDetector.detectChanges();
        return result;
    }

    dirtyCheckCallback(result: IDirtyFormResult): void {

        if (result.isSkipSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }

        if (result.isSave) {
            if (this.isModalActive) {
                this.activeModal.dismiss();
            }
        }
    }

    dismiss() {
        if (this.dirtyForm.canClose()) {
            this.activeModal.dismiss();
        }
    }

    getDirtyStatus(guid: string): boolean {
        if (this.mallbodys) {
            const found = this.mallbodys.find(x => x.mall.guid === guid);
            if (found) {
                return found.isDirty;
            }
        }

        return false;
    }

    isAllowedToAddRows(): boolean {
        return ((this.mallar === null || this.mallar === undefined || this.mallar.length === 0) || !this.mallar.some(mall => mall.isNew));
    }

    isMallDefaultExpanded(mall: IProduktionsmall): boolean {
        if (mall.isNew) {
            return true;
        }

        if (mall.guid === this.lastUpdatedMallGuid) {
            return true;
        }

        return false;
    }

    isModalDirty(): boolean {
        return this.mallbodys.some(x => x.isDirty);
    }

    ngOnInit() {
        const routeData = this.route.snapshot.data as ProduktionsmallarRouteData;
        this.isCKMall = routeData.isCKMall;
        this.produktionsmallarService.refreshProduktionsomraden();
        this.produktionsmallarService.refreshHandelssortimentGrupper();

        this.busyService.addWork(this.produktionsmallarService.getAllMallar(this.isCKMall)).subscribe(x => {
            this.mallar = this.sortMallar(x);
        });

        this.dirtyForm.onDirtyNgModel(
            this.isModalDirty.bind(this),
            this.saveAll.bind(this),
            d => this.dirtyCheckCallback(d),
            () => this.canMallarBeSaved(),
            this.texts.lamnaModalHeader,
            this.texts.lamnaModalBody,
            { size: "lg",
                centered: false });
    }

    refresh(refreshItem: IProduktionsmallRefreshItem): void {
        if (refreshItem.mode === ProduktionsmallRefreshMode.Delete) {
            this.mallar = this.mallar.filter(cmall => cmall.guid !== refreshItem.mall.guid);
            this.sortMallar(this.mallar);
            this.produktionsmallarService.refreshProduktionsmallar();
        } else {
            this.busyService.addWork(this.produktionsmallarService.getAllMallar(this.isCKMall)).subscribe(mallarFromDB => {
                this.saveModifiedMall(refreshItem.mall, mallarFromDB);
                this.sortMallar(this.mallar);
            });
        }
    }

    saveAll() {
        this.mallbodys.forEach(x => x.saveAll(false));
    }

    sortMallar(mallar: IProduktionsmall[]): IProduktionsmall[] {
        return mallar.sort((a, b) => moment(b.uDate).unix() - moment(a.uDate).unix());
    }

    private saveModifiedMall(mall: IProduktionsmall, mallarFromDB: IProduktionsmall[]) {
        this.mallar = this.mallar.filter(cmall => cmall.guid !== mall.guid);
        const modifiedFromServer = mallarFromDB.find(nmall => nmall.guid === mall.guid);
        this.lastUpdatedMallGuid = modifiedFromServer.guid;
        this.mallar.push(modifiedFromServer);
    }
}
